import {useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams, Redirect} from 'react-router-dom';
import gql from 'graphql-tag';
import {useMutation} from 'services/apollo';
import {EzButton, EzHeading, EzLayout, EzPage} from '@ezcater/recipe';
import {
  DriverSelfAssignErrorCode,
  type SelfAssignMutation,
  type SelfAssignMutationVariables,
} from 'services/graphql/types';
import {useTranslation} from 'react-i18next';
import {useCurrentAccount} from 'components/CurrentAccount';
import {DELIVERIES_PATH, REQUEST_SIGN_IN_LINK_PATH} from 'paths';
import {SELF_ASSIGN_PAGE_NAME} from 'util/tracking/trackingEvents';
import {trackPageView} from 'util/tracking/trackEvent';
import Layout from '../Layout';
import {getAdaptedSelfAssignSource} from '../../util/urlUtils';

export const AssignMutation = gql`
  mutation SelfAssignMutation($input: DriverDeliverySelfAssignInput!) {
    selfAssign: driverDeliverySelfAssign(input: $input) {
      assignment {
        id
      }
      userErrors {
        ... on UserError {
          message
        }
        ... on DriverSelfAssignError {
          code
          message
        }
      }
    }
  }
`;

type SelfAssignBodyProps = {
  deliveryId: string;
};

const SelfAssignBody = ({deliveryId}: SelfAssignBodyProps) => {
  const {t} = useTranslation();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [userErrors, setUserErrors] = useState<
    NonNullable<SelfAssignMutation['selfAssign']>['userErrors']
  >([]);
  const history = useHistory();
  const {search} = useLocation();
  const source = useMemo(
    () => getAdaptedSelfAssignSource(new URLSearchParams(search).get('source')),
    [search]
  );

  const [selfAssign, selfAssignQueryResult] = useMutation<
    SelfAssignMutation,
    SelfAssignMutationVariables
  >(AssignMutation, {
    onError: e => console.error(e),
  });
  const assignmentId = selfAssignQueryResult.data?.selfAssign?.assignment?.id;

  useEffect(() => {
    const executeMutation = async () => {
      try {
        const mutationResult = await selfAssign({variables: {input: {deliveryId, source}}});
        setUserErrors(mutationResult?.data?.selfAssign?.userErrors || []);
      } finally {
        setIsInitialLoading(false);
      }
    };

    executeMutation();
  }, [deliveryId, selfAssign, source]);

  if (assignmentId) {
    return <Redirect to={`/assignment/${assignmentId}`} />;
  }

  const handleForceSelfAssign = async (): Promise<void> => {
    const mutationResult = await selfAssign({
      variables: {input: {deliveryId, replace: true, source}},
    });
    setUserErrors(mutationResult?.data?.selfAssign?.userErrors || []);
  };

  const handleAssignCancelClick = (): void => {
    history.push(DELIVERIES_PATH);
  };

  const hasErrors = !!selfAssignQueryResult.error || !!userErrors?.length;
  const hasValidationUserError = userErrors?.some(e => e.__typename === 'ValidationError');
  const selfAssignUserErrors = userErrors?.filter(e => e.__typename === 'DriverSelfAssignError');
  const hasAnotherDriverAssigned = userErrors?.some(
    e => e.__typename === 'DriverSelfAssignError' && e.code === DriverSelfAssignErrorCode.Assigned
  );

  return (
    <Layout title={t('selfAssign.title')} loading={isInitialLoading}>
      <EzPage>
        {hasErrors && (
          <>
            <EzHeading size="2">{t('selfAssign.assignErrorTitle')}</EzHeading>
            {(selfAssignQueryResult.error || hasValidationUserError) && (
              <EzHeading size="4">{t('selfAssign.assignErrorDefaultMessage')}</EzHeading>
            )}
            {selfAssignUserErrors?.map((error, index) => (
              <EzHeading key={index} size="4">
                {error.message}
              </EzHeading>
            ))}
            {hasAnotherDriverAssigned && (
              <EzLayout layout="stack" alignX="center">
                <EzButton
                  disabled={selfAssignQueryResult.loading}
                  onClick={handleForceSelfAssign}
                  use="primary"
                  legacy
                >
                  {t('selfAssign.replaceConfirmButtonText')}
                </EzButton>
                <EzButton use="secondary" onClick={handleAssignCancelClick} legacy>
                  {t('selfAssign.replaceCancelButtonText')}
                </EzButton>
              </EzLayout>
            )}
          </>
        )}
      </EzPage>
    </Layout>
  );
};

type RouteParams = {
  deliveryId: string;
};

const SelfAssign = () => {
  const {deliveryId} = useParams<RouteParams>();
  const {id} = useCurrentAccount();

  useEffect(() => {
    trackPageView(SELF_ASSIGN_PAGE_NAME, {
      driver_id: id,
      delivery_id: deliveryId,
    });
  }, [deliveryId, id]);

  // If someone bypasses auth by removing the `source` URL query parameter,
  // redirect and assume source is "qrcode" so they can request a new sign in
  // link.
  if (!id) {
    const redirectTo = `${REQUEST_SIGN_IN_LINK_PATH}?deliveryId=${deliveryId}&source=qrcode`;

    return <Redirect to={redirectTo} />;
  }

  return <SelfAssignBody deliveryId={deliveryId} />;
};

export default SelfAssign;
